import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  Title,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import PullToRefresh from 'react-simple-pull-to-refresh';
import { LocalNotifications } from '@capacitor/local-notifications';
import "./Theme.css";
import SmsListener from "./SmsListener";
import ReviewView from "./ReviewView";
import SetupView from "./SetupView";
import { LayoutDashboard, List, Clock, Inbox, Plus, Check, Loader2, ArrowDown, Bell, BellOff, X } from 'lucide-react';

const API_BASE_URL = "https://my-finance.pksystems.in/api";

function App() {
  console.log("App Component Rendering...");
  console.log("Purple Theme v4.0 Loaded");

  ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    CategoryScale,
    LinearScale,
    Title,
    BarElement
  );
  // Setup State
  const [isSetupComplete, setIsSetupComplete] = useState(localStorage.getItem('setupDone') === 'true');
  console.log("isSetupComplete:", isSetupComplete);

  const [stats, setStats] = useState({});
  const [messages, setMessages] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Graph State
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showDailyModal, setShowDailyModal] = useState(false);

  // UI State
  const [activeTab, setActiveTab] = useState("dashboard"); // 'dashboard' | 'transactions' | 'pending' | 'inbox'
  const [showCompletedPending, setShowCompletedPending] = useState(false);

  // Inbox / Unreviewed
  const [unreviewedMessages, setUnreviewedMessages] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]); // All SMS for inbox

  // Edit State
  const [editingId, setEditingId] = useState(null); // ID of currently editing transaction
  const [hasMore, setHasMore] = useState(true); // For infinite scroll
  const [modifications, setModifications] = useState({}); // { [id]: { category?, type? } }

  // Manual Review / SMS State
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [reviewResult, setReviewResult] = useState(null);
  const [analyzingText, setAnalyzingText] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false); // Track if opened via FAB

  // Pending Payment Add State
  const [showAddPendingModal, setShowAddPendingModal] = useState(false);
  const [newPending, setNewPending] = useState({ merchant: '', amount: '', dueDate: '' });
  const [disabledReminders, setDisabledReminders] = useState(() => {
    try { return JSON.parse(localStorage.getItem('disabledReminders') || '[]'); } catch { return []; }
  });


  // Initial Load (Restored to top)
  useEffect(() => {
    fetchStats();
    fetchMessages(0);
    fetchPending();
    fetchUnreviewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ActiveTab Listener (Restored to top)
  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPending();
    }
    if (activeTab === 'inbox') {
      fetchUnreviewed();
      fetchInboxMessages(); // Fetch all SMS for inbox
    }
    document.addEventListener('deviceready', () => {
      if (window.cordova && window.cordova.plugins && window.cordova.plugins.backgroundMode) {
        window.cordova.plugins.backgroundMode.overrideBackButton();
      }
    }, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCompletedPending, activeTab]);


  // If setup is not complete, show the setup wizard
  if (!isSetupComplete) {
    return <SetupView onComplete={() => {
      localStorage.setItem('setupDone', 'true');
      setIsSetupComplete(true);
    }} />;
  }

  // Restore fetchStats
  async function fetchStats() {
    try {
      const res = await axios.get(`${API_BASE_URL}/stats`);
      setStats(res.data);
      const monthRes = await axios.get(`${API_BASE_URL}/stats/monthly`);
      setMonthlyStats(monthRes.data);
    } catch (e) {
      console.error("Fetch stats failed", e);
      alert("Failed to load stats: " + e.message);
    }
  };

  // Infinite Scroll Handler
  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50; // 50px buffer
    if (bottom && !loading && hasMore) {
      fetchMessages(page + 1);
    }
  };

  async function fetchMessages(p) {
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/messages?page=${p}&size=20`);
      if (res.data.length === 0 && p > 0) {
        setHasMore(false);
      } else {
        setMessages(prev => p === 0 ? res.data : [...prev, ...res.data]);
        setPage(p);
      }
    } catch (e) {
      console.error("Fetch messages failed", e);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (id, tx) => {
    setEditingId(id);
    setModifications({
      [id]: {
        category: tx.category,
        type: tx.transactionType || tx.transaction_type || 'OUTFLOW'
      }
    });
  };

  const saveSingleEdit = async (id) => {
    const changes = modifications[id];
    if (!changes) {
      setEditingId(null);
      return;
    }

    try {
      if (changes.category) {
        await axios.post(`${API_BASE_URL}/sms/${id}/category`, { category: changes.category });
      }
      if (changes.type) {
        await axios.post(`${API_BASE_URL}/sms/${id}/type`, { type: changes.type });
      }

      // Refresh data from server to get latest values
      setEditingId(null);
      setModifications({});
      fetchMessages(0); // Refresh from server
      fetchStats(); // Refresh stats in background
    } catch (e) {
      alert("Failed to save: " + e.message);
    }
  };

  // ... skip fetchPending, fetchUnreviewed (unchanged) ...

  // Cache busting for fetch requests
  async function fetchPending() {
    try {
      const res = await axios.get(`${API_BASE_URL}/pending-payments?includeDone=${showCompletedPending}&t=${Date.now()}`);
      setPendingPayments(res.data);
    } catch (e) {
      console.error("Fetch pending failed", e);
    }
  };

  async function fetchUnreviewed() {
    try {
      const res = await axios.get(`${API_BASE_URL}/sms/unreviewed?t=${Date.now()}`);
      setUnreviewedMessages(res.data);
    } catch (e) {
      console.error("Fetch unreviewed failed", e);
    }
  };

  async function fetchInboxMessages() {
    try {
      const res = await axios.get(`${API_BASE_URL}/sms/all?limit=100&t=${Date.now()}`);
      setInboxMessages(res.data);
    } catch (e) {
      console.error("Fetch inbox messages failed", e);
    }
  };

  const markPendingDone = async (id) => {
    try {
      await axios.post(`${API_BASE_URL}/pending-payments/${id}/done`);
      cancelReminders({ id });
      fetchPending();
    } catch (err) {
      alert("Failed to mark as done");
    }
  };

  const saveNewPendingPayment = async () => {
    if (!newPending.merchant || !newPending.amount) {
      alert("Merchant and Amount are required");
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/pending-payments`, newPending);
      setShowAddPendingModal(false);

      // Schedule Reminders (Aggressive)
      if (res.data && res.data.id) {
        scheduleAggressiveReminders(res.data);
      } else {
        scheduleAggressiveReminders({ ...newPending, id: Date.now() });
      }

      setNewPending({ merchant: '', amount: '', dueDate: '' });
      fetchPending();
      alert("Pending payment added & Reminders scheduled");
    } catch (e) {
      alert("Failed to add pending payment: " + e.message);
    }
  };

  const handleSmsReceived = async (e) => {
    console.log("App received SMS:", e);
    try {
      // Use Android SMS _id if available, otherwise fall back to timestamp
      const smsId = e._id || e.id || e.date;

      const payload = {
        id: smsId,              // Use actual SMS ID from Android
        sender: e.address,
        body: e.body,
        timestamp: e.date       // Keep timestamp for sorting
      };

      console.log("📱 Sending SMS to backend:", {
        id: smsId,
        sender: e.address,
        timestamp: e.date,
        bodyLength: e.body?.length
      });

      await axios.post(`${API_BASE_URL}/sms/incoming`, payload);
      alert("New SMS from " + e.address);
    } catch (err) {
      console.error("Failed to sync incoming SMS", err);
    }
    fetchUnreviewed();
  };

  const analyzeReviewText = async (textOverride = null) => {
    const textToAnalyze = typeof textOverride === 'string' ? textOverride : reviewText;
    if (!textToAnalyze) return;

    // Preserve the original SMS ID if it exists
    const originalSmsId = reviewResult?.id;

    setAnalyzingText(true);
    try {
      const tempMsg = {
        id: originalSmsId || Date.now(),
        body: textToAnalyze,
        sender: "Manual",
        timestamp: Date.now()
      };
      const res = await axios.post(`${API_BASE_URL}/analyze?batchSize=1`, [tempMsg]);
      if (res.data && res.data.length > 0) {
        // Preserve the original SMS ID if it existed
        const analyzed = originalSmsId
          ? { ...res.data[0], id: originalSmsId }
          : res.data[0];
        setReviewResult(analyzed);
      }
    } catch (e) {
      alert("Analysis Failed: " + e.message);
    } finally {
      setAnalyzingText(false);
    }
  };

  const saveManualReview = async (data, text) => {
    const resultToSave = data || reviewResult;
    const bodyToSave = text || reviewText;

    if (!resultToSave) return;
    try {
      const payload = { ...resultToSave, body: bodyToSave };
      await axios.post(`${API_BASE_URL}/manual-entry`, payload);

      setReviewModalOpen(false);
      setReviewText("");
      setReviewResult(null);
      localStorage.removeItem('pendingSms');

      fetchStats();
      fetchMessages(page);
      fetchPending();

      // Delay to ensure backend transaction completes before refreshing
      setTimeout(() => {
        fetchUnreviewed();
      }, 300);

      alert("Saved successfully!");
    } catch (e) {
      alert("Save Failed: " + e.message);
    }
  };

  const handleEditChange = (id, field, value) => {
    setModifications(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  // Removed bulk edit saveChanges/cancelEdit




  const totalSpend = stats.totalSpend || 0;
  const totalInvest = stats.totalInvest || 0;
  const totalPayments = stats.totalPayments || 0;
  const catTotals = stats.categorySpend || {};

  const pieData = {
    labels: Object.keys(catTotals),
    datasets: [{
      data: Object.values(catTotals),
      backgroundColor: ['#f59e0b', '#3b82f6', '#ec4899', '#6366f1', '#8b5cf6', '#64748b', '#ef4444', '#10b981'],
    }],
  };




  // Process Monthly Stats for Bar Chart
  // We need to group by Month -> { Spend, Invest, Payment }
  const monthlyDataMap = {};
  monthlyStats.forEach(item => {
    if (!monthlyDataMap[item.month]) {
      monthlyDataMap[item.month] = { spend: 0, invest: 0, payment: 0 };
    }
    const amount = item.total || 0;
    const cat = item.category;
    const type = item.transaction_type;

    if (type === 'PAYMENT') {
      monthlyDataMap[item.month].payment += amount;
    } else if (cat === 'Investment' || cat === 'Investments') {
      monthlyDataMap[item.month].invest += amount;
    } else {
      monthlyDataMap[item.month].spend += amount;
    }
  });

  const sortedMonths = Object.keys(monthlyDataMap).sort(); // YYYY-MM sort works
  const barData = {
    labels: sortedMonths,
    datasets: [
      {
        label: 'Spend',
        data: sortedMonths.map(m => monthlyDataMap[m].spend),
        backgroundColor: '#ef4444',
      },
      {
        label: 'Invest',
        data: sortedMonths.map(m => monthlyDataMap[m].invest),
        backgroundColor: '#10b981',
      },
      {
        label: 'Payments',
        data: sortedMonths.map(m => monthlyDataMap[m].payment),
        backgroundColor: '#3b82f6',
      },
    ],
  };

  const handleGraphClick = async (event, elements) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const month = sortedMonths[index];
      setSelectedMonth(month);

      try {
        const res = await axios.get(`${API_BASE_URL}/stats/daily?month=${month}`);
        setDailyStats(res.data);
        setShowDailyModal(true);
      } catch (e) {
        alert("Failed to load daily stats");
      }
    }
  };

  const categoryOptions = [
    "Food & Dining", "Groceries", "Travel", "Shopping", "Bills & Utilities",
    "Entertainment", "Healthcare", "Investments", "Banking", "Personal Care",
    "Education", "Fitness", "Others", "Cashback", "Non-Financial"
  ];

  const typeOptions = ["INFLOW", "OUTFLOW", "PAYMENT", "CASHBACK", "IRRELEVANT"];

  const getDisplayValue = (tx, field) => {
    const id = tx.id || tx.sms_id;
    if (modifications[id] && modifications[id][field]) {
      return modifications[id][field];
    }
    if (field === 'type') {
      // If backend returns null/undefined, default to OUTFLOW.
      // If backend returns INFLOW, return INFLOW.
      // Fix: Backend returns camelCase 'transactionType', local state might use snake_case
      return tx.transactionType || tx.transaction_type || 'OUTFLOW';
    }
    return tx.category;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'INFLOW': return '#10b981';
      case 'OUTFLOW': return '#ef4444';
      case 'PAYMENT': return '#3b82f6';
      case 'CASHBACK': return '#8b5cf6';
      default: return '#64748b';
    }
  };



  // --- Reminder Logic ---
  const toggleReminder = async (payment) => {
    const id = payment.id;
    const isDisabled = disabledReminders.includes(id);

    if (isDisabled) {
      // Enable
      const newSet = disabledReminders.filter(d => d !== id);
      setDisabledReminders(newSet);
      localStorage.setItem('disabledReminders', JSON.stringify(newSet));
      scheduleAggressiveReminders(payment);
      alert("Reminders Enabled");
    } else {
      // Disable
      const newSet = [...disabledReminders, id];
      setDisabledReminders(newSet);
      localStorage.setItem('disabledReminders', JSON.stringify(newSet));
      cancelReminders(payment);
      alert("Reminders Cancelled");
    }
  };

  const cancelReminders = async (payment) => {
    const baseId = (typeof payment.id === 'string' ? parseInt(payment.id.replace(/\D/g, '') || '0') : payment.id) * 1000;
    const ids = [];
    for (let i = 0; i < 50; i++) ids.push({ id: baseId + i });
    await LocalNotifications.cancel({ notifications: ids });
  };

  const scheduleAggressiveReminders = async (payment) => {
    try {
      console.log("🔔 Starting notification scheduling for:", payment);

      // Don't schedule notifications for completed payments
      if (payment.is_done) {
        console.log("⏭️ Skipping notification scheduling - payment already marked as done");
        return;
      }

      // Check permissions
      let perm = await LocalNotifications.checkPermissions();
      console.log("📱 Current notification permissions:", perm);

      if (perm.display !== 'granted') {
        console.log("⚠️ Requesting notification permissions...");
        perm = await LocalNotifications.requestPermissions();
        console.log("📱 Permission request result:", perm);

        if (perm.display !== 'granted') {
          alert("⚠️ Notification permissions denied. Reminders won't work!");
          return;
        }
      }

      const dueDate = payment.due_date ? new Date(payment.due_date) : new Date();
      if (typeof payment.due_date === 'string') {
        const parts = payment.due_date.split('-');
        if (parts.length === 3) dueDate.setFullYear(parts[0], parts[1] - 1, parts[2]);
      }

      console.log("📅 Due date parsed:", dueDate);

      const now = new Date();
      const notifications = [];
      const baseId = (typeof payment.id === 'string' ? parseInt(payment.id.replace(/\D/g, '') || '0') : payment.id) * 1000;

      console.log("🆔 Base notification ID:", baseId);

      await cancelReminders(payment);

      // 1. Pre-Alert: 1 Day before 11:00 AM
      const preAlert = new Date(dueDate);
      preAlert.setDate(dueDate.getDate() - 1);
      preAlert.setHours(11, 0, 0, 0);

      if (preAlert > now) {
        notifications.push({
          id: baseId + 1,
          title: `Upcoming: ${payment.merchant}`,
          body: `Due Tomorrow: ₹${payment.amount}`,
          schedule: { at: preAlert }
        });
      }

      // 2. Due Date Blitz: 10 AM - Midnight
      for (let h = 10; h <= 24; h++) {
        const blitz = new Date(dueDate);
        blitz.setHours(h === 24 ? 0 : h, 0, 0, 0);
        if (h === 24) blitz.setDate(blitz.getDate() + 1);

        if (blitz > now) {
          notifications.push({
            id: baseId + 100 + h,
            title: `PAY NOW: ${payment.merchant}`,
            body: `Due NOW! ₹${payment.amount}`,
            schedule: { at: blitz }
          });
        }
      }

      // 3. Overdue: Tomorrow Daily
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      if (tomorrow > dueDate) {
        for (let h = 11; h <= 24; h++) {
          const overdue = new Date(tomorrow);
          overdue.setHours(h === 24 ? 0 : h, 0, 0, 0);
          if (h === 24) overdue.setDate(overdue.getDate() + 1);

          notifications.push({
            id: baseId + 200 + h,
            title: `OVERDUE: ${payment.merchant}`,
            body: `Please pay ₹${payment.amount}`,
            schedule: { at: overdue }
          });
        }
      }

      if (notifications.length > 0) {
        console.log(`✅ Scheduling ${notifications.length} reminders for ${payment.merchant}`);
        console.log("📋 First notification:", notifications[0]);
        await LocalNotifications.schedule({ notifications });
        console.log("✅ Notifications scheduled successfully!");
      } else {
        console.log("⚠️ No notifications to schedule (all times in the past)");
      }
    } catch (error) {
      console.error("❌ Error scheduling notifications:", error);
      alert(`Failed to schedule reminders: ${error.message}`);
    }
  };

  // Pull to Refresh Handler
  async function handleRefresh() {
    // Artificial delay for UX
    const fetchPromises = [new Promise(resolve => setTimeout(resolve, 800))];

    if (activeTab === 'dashboard') {
      fetchPromises.push(fetchStats());
      fetchPromises.push(fetchMessages(0));
    }
    if (activeTab === 'transactions') {
      fetchPromises.push(fetchMessages(0));
    }
    if (activeTab === 'pending') {
      fetchPromises.push(fetchPending());
    }
    if (activeTab === 'inbox') {
      fetchUnreviewed();
    }
    // Always fetch unreviewed/pending in background anyway
    fetchUnreviewed();

    await Promise.all(fetchPromises);
  }

  const headerTitles = {
    dashboard: { pre: "Welcome back", title: "Pranay" },
    transactions: { pre: "History", title: "All Transactions" },
    pending: { pre: "Upcoming", title: "Pending Payments" },
    inbox: { pre: "Review", title: "SMS Inbox" }
  };
  const currentHeader = headerTitles[activeTab] || headerTitles.dashboard;

  return (
    <div className="app-container">
      <SmsListener onSmsReceived={handleSmsReceived} />

      {showDailyModal && (
        <div className="modal-overlay" onClick={() => setShowDailyModal(false)}>
          <div className="modal-content full-height" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3>Daily Breakdown: {selectedMonth}</h3>
              <button onClick={() => setShowDailyModal(false)} className="close-btn"><X size={24} /></button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {dailyStats.map((day, idx) => (
                <div key={idx} className="tx-item">
                  <div className="tx-details">
                    <div className="tx-merchant">{day.day}</div>
                    <div className="tx-meta">{day.category} • {day.transaction_type || 'Expense'}</div>
                  </div>
                  <div className="tx-amount">₹{day.total?.toLocaleString()}</div>
                </div>
              ))}
              {dailyStats.length === 0 && <p style={{ textAlign: 'center', padding: 20 }}>No data found.</p>}
            </div>
          </div>
        </div>
      )}

      {reviewModalOpen && (
        <ReviewView
          initialText={reviewText}
          reviewResult={reviewResult}
          analyzing={analyzingText}
          onAnalyze={analyzeReviewText}
          onSave={saveManualReview}
          onCancel={() => {
            setReviewResult(null);
            setReviewText("");
            setReviewModalOpen(false);
            localStorage.removeItem('pendingSms');
          }}
          isManual={isManualEntry}
        />
      )}

      <div className="tabs">
        <div
          className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <LayoutDashboard size={24} />
        </div>
        <div
          className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          <List size={24} />
        </div>
        <div
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          <Clock size={24} />
        </div>
        <div className={`tab ${activeTab === 'inbox' ? 'active' : ''}`} onClick={() => setActiveTab('inbox')}>
          <div style={{ position: 'relative', display: 'inline-flex', overflow: 'visible' }}>
            <Inbox size={24} />
            {unreviewedMessages.length > 0 && (
              <span style={{
                top: '-8px',
                right: '-10px',
                position: 'absolute',
                zIndex: 10,
                minWidth: '20px',
                height: '20px',
                padding: '0 5px',
                borderRadius: '10px',
                fontSize: '0.65rem',
                fontWeight: 'bold',
                color: '#ffffff',
                backgroundColor: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                whiteSpace: 'nowrap',
                border: '2px solid white',
                boxSizing: 'border-box'
              }}>
                {unreviewedMessages.length > 999 ? '999+' : unreviewedMessages.length}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Context-Aware FAB */}
      <button className="fab" onClick={() => {
        if (activeTab === 'pending') {
          setNewPending({ merchant: '', amount: '', dueDate: '' });
          setShowAddPendingModal(true);
        } else {
          setReviewText("");
          setReviewResult(null);
          setIsManualEntry(true);
          setReviewModalOpen(true);
        }
      }}>
        <Plus size={28} />
      </button>

      {/* 1. Purple Header Area */}
      <div className="app-header">
        <div className="welcome-text">{currentHeader.pre}</div>
        <div className="app-title">{currentHeader.title}</div>

        {/* Only show Summary Cards on Dashboard */}
        {activeTab === 'dashboard' && (
          <div className="header-cards">
            <div className="glass-card">
              <div className="glass-label">Total Spend</div>
              <div className="glass-value">₹{totalSpend.toLocaleString()}</div>
            </div>
            <div className="glass-card">
              <div className="glass-label">Invested</div>
              <div className="glass-value">₹{totalInvest.toLocaleString()}</div>
            </div>
            <div className="glass-card">
              <div className="glass-label">Total Payments</div>
              <div className="glass-value">₹{totalPayments.toLocaleString()}</div>
            </div>
          </div>
        )}
      </div>

      {/* 2. White Sheet Area - Scroller */}
      <div className="sheet-container">
        <div className="sheet-content" onScroll={activeTab === 'transactions' ? handleScroll : undefined}>
          <PullToRefresh
            onRefresh={handleRefresh}
            pullingContent={
              <div style={{ padding: 15, textAlign: 'center', opacity: 0.6 }}>
                <ArrowDown size={20} color="#6b7280" />
              </div>
            }
            refreshingContent={
              <div style={{ padding: 15, textAlign: 'center' }}>
                <Loader2 size={20} className="spinner" />
              </div>
            }
          >
            <div>
              {activeTab === 'inbox' && (
                <div className="mobile-tx-list">
                  <div className="section-title" style={{ display: 'flex', justifyContent: 'flex-end', minHeight: '20px' }}>
                    {/* Badge Removed */}
                  </div>
                  {inboxMessages.length === 0 ? (
                    <div className="empty-state">
                      <p>No SMS found.</p>
                    </div>
                  ) : (
                    inboxMessages.map(msg => {
                      const isReviewed = msg.reviewed; // Use backend-provided reviewed status

                      return (
                        <div key={msg.id} className="tx-item" style={{ opacity: isReviewed ? 0.75 : 1 }} onClick={() => {
                          setReviewText(msg.body);
                          setReviewResult({ id: msg.id, body: msg.body });
                          setIsManualEntry(false);
                          setReviewModalOpen(true);
                        }}>
                          <div className="tx-icon" style={{ background: isReviewed ? '#d1fae5' : '#e0f2fe', color: isReviewed ? '#059669' : '#0284c7' }}>
                            <Inbox size={24} />
                          </div>
                          <div className="tx-details">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div className="tx-merchant">{msg.sender || 'Unknown'}</div>
                              {isReviewed && (
                                <span style={{ background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.65rem', fontWeight: '600' }}>REVIEWED</span>
                              )}
                            </div>
                            <div className="tx-meta">{msg.date || msg.readableDate}</div>
                            <div className="tx-meta clean-text">{msg.body}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {activeTab === 'dashboard' && (
                <>
                  <div className="section-title">Analytics</div>
                  <div className="chart-container-sheet">
                    <Doughnut data={pieData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { usePointStyle: true, boxWidth: 8 } } } }} />
                  </div>

                  <div className="section-title">Monthly Trends</div>
                  <div className="chart-container-sheet" style={{ height: '250px' }}>
                    <Bar
                      data={barData}
                      options={{
                        maintainAspectRatio: false,
                        responsive: true,
                        scales: { x: { stacked: true }, y: { stacked: true } },
                        onClick: handleGraphClick
                      }}
                    />
                  </div>
                </>
              )}

              {(activeTab === 'dashboard' || activeTab === 'transactions') && (
                <div className="mobile-tx-list">
                  {activeTab === 'dashboard' && (
                    <div className="section-title">Recent Transactions</div>
                  )}
                  {messages.map((tx) => {
                    const id = tx.id || tx.sms_id;
                    const isEditing = editingId === id;
                    const tType = getDisplayValue(tx, 'type');
                    const cat = getDisplayValue(tx, 'category');

                    return (
                      <div className="tx-item" key={id} onClick={(e) => {
                        if (!isEditing) startEdit(id, tx);
                      }}>
                        <div className={`tx-icon ${tType === 'INFLOW' ? 'income' : tType === 'OUTFLOW' ? 'expense' : ''}`}>
                          {tType === 'INFLOW' ? <Check size={24} /> : <div style={{ fontWeight: 'bold' }}>₹</div>}
                        </div>
                        <div className="tx-details">
                          <div className="tx-merchant">{isEditing ? 'Editing...' : tx.merchant}</div>
                          <div className="tx-meta">{new Date(tx.sms_timestamp || tx.date).toLocaleDateString()} • {cat}</div>

                          {isEditing && (
                            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <select value={tType} onChange={(e) => handleEditChange(id, 'type', e.target.value)} className="modern-input" onClick={e => e.stopPropagation()} style={{ padding: '8px', flex: 1 }}>
                                  {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <select value={cat} onChange={(e) => handleEditChange(id, 'category', e.target.value)} className="modern-input" onClick={e => e.stopPropagation()} style={{ padding: '8px', flex: 1 }}>
                                  {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingId(null); }}
                                  style={{
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                  title="Cancel"
                                >
                                  <X size={20} />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); saveSingleEdit(id); }}
                                  style={{
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                  title="Save"
                                >
                                  <Check size={20} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        {!isEditing && (
                          <div className={`tx-amount`} style={{ color: getTypeColor(tType) }}>
                            {tType === 'INFLOW' ? '+' : '-'}₹{tx.amount}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {loading && <div className="empty-state">Loading more...</div>}
                </div>
              )}

              {activeTab === 'pending' && (
                <div className="mobile-tx-list">
                  <div className="section-title" style={{ justifyContent: 'flex-end' }}>
                    {/* Toggle Switch */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Show Completed</span>
                      <div
                        className={`toggle-switch ${showCompletedPending ? 'on' : ''}`}
                        onClick={() => setShowCompletedPending(!showCompletedPending)}
                      >
                        <div className="toggle-thumb" />
                      </div>
                    </div>
                  </div>

                  {pendingPayments.length === 0 ? (
                    <div className="empty-state">No pending payments found.</div>
                  ) : (
                    pendingPayments.map(p => (
                      <div className="tx-item" key={p.id} style={{ opacity: p.is_done ? 0.6 : 1 }}>
                        <div className="tx-icon" style={{ background: '#fff7ed', color: '#ea580c' }}>
                          <Clock size={24} />
                        </div>
                        <div className="tx-details">
                          <div className="tx-merchant">{p.merchant || "Unknown"}</div>
                          <div className="tx-meta">Due: {p.due_date} • {p.is_done ? "Paid" : "Pending"}</div>
                          <div className="tx-meta clean-text">{p.original_body || "Manual Entry"}</div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                          <div className="tx-amount" style={{ color: '#ea580c' }}>₹{p.amount}</div>
                          {!p.is_done && (
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                              <div onClick={(e) => { e.stopPropagation(); toggleReminder(p); }} style={{ color: disabledReminders.includes(p.id) ? '#9ca3af' : '#7c3aed' }}>
                                {disabledReminders.includes(p.id) ? <BellOff size={20} /> : <Bell size={20} />}
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); markPendingDone(p.id); }}
                                style={{
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '40px',
                                  height: '40px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                title="Mark as Done"
                              >
                                <Check size={20} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </PullToRefresh>
        </div>
      </div>

      {/* Add Pending Modal */}
      {
        showAddPendingModal && (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAddPendingModal(false) }}>
            <div className="modal-card">
              <h3 className="modal-title">New Pending Bill</h3>
              <div className="form-group">
                <label>Merchant / Title</label>
                <input type="text" className="modern-input" value={newPending.merchant} onChange={e => setNewPending({ ...newPending, merchant: e.target.value })} placeholder="e.g. Electricity Bill" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Amount (₹)</label>
                  <input type="number" className="modern-input" value={newPending.amount} onChange={e => setNewPending({ ...newPending, amount: e.target.value })} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" className="modern-input" value={newPending.dueDate} onChange={e => setNewPending({ ...newPending, dueDate: e.target.value })} />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-primary" onClick={saveNewPendingPayment}>Save</button>
                <button className="btn-secondary" onClick={() => setShowAddPendingModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )
      }

      {/* Tabs */}
      <div className="tabs">
        <div className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
          <LayoutDashboard size={24} />
        </div>
        <div className={`tab ${activeTab === 'transactions' ? 'active' : ''}`} onClick={() => setActiveTab('transactions')}>
          <List size={24} />
        </div>
        <div className={`tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          <Clock size={24} />
        </div>
        <div className={`tab ${activeTab === 'inbox' ? 'active' : ''}`} onClick={() => setActiveTab('inbox')}>
          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <Inbox size={24} />
            {unreviewedMessages.length > 0 && <span className="icon-badge" style={{ top: '-5px', right: '-10px' }}>{unreviewedMessages.length}</span>}
          </div>
        </div>
      </div>

    </div >
  );
}

export default App;
